'use client'

import React from 'react'
import styled, { css } from 'styled-components'
import useDropdownEvents, { CloseDropdownProps } from './useDropdownEvents'
import DropdownPortal from './DropdownPortal'

export type DropdownTriggerProps = {
  toggleDropdown: () => void
  isOpen?: boolean
  position: NonHTMLProps['position']
}

const DropdownContainer = styled.div<Partial<NonHTMLProps>>`
  width: ${(props) => (props.fillParent ? '100%' : 'auto')};
  position: relative;
  display: flex;
  justify-content: ${(props) => (props.position === 'right' ? 'flex-end' : 'flex-start')};
`

const DropdownBackdrop = styled.div<
  Partial<NonHTMLProps & DropdownTriggerProps>
>`
  display: flex;
  align-items: flex-start;
  transition: background-color 100ms;
  ${(props) =>
    props.fillParent &&
    css`
      padding: 15px;
      position: absolute;
      background-color: transparent;
      left: 0;
      right: 0;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 100%;
    `}
  ${(props) =>
    props.isOpen &&
    props.fillParent &&
    css`
      background-color: var(--dropdown-backdrop);
    `}
`

type DropdownListProps = Pick<
  NonHTMLProps,
  'fillParent' | 'position' | 'inlineOffset' | 'blockOffset'
> & {
  isOpen: boolean
  usePortal?: boolean
  isProductCard?: boolean
}

export const DropdownList = styled.div<DropdownListProps>`
  ${(props: DropdownListProps) => {
    if (!props.usePortal) {
      return css`
        position: absolute;
        top: calc(100% + ${props.blockOffset}px);
      `
    }
    return ''
  }}
  background-color: var(--white);
  width: ${(props) => (props.fillParent ? '100%' : 'auto')};
  border-radius: var(--border-radius-sm);
  padding: 5px;
  border: 1px solid var(--gray200);
  z-index: 8;
  box-shadow: hsl(206 22% 7% / 35%) 0px 10px 38px -10px, hsl(206 22% 7% / 20%) 0px 10px 20px -15px;
  animation-duration: 400ms;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform, opacity;
  transition: transform 0.2s linear, opacity 0.2s linear, visibility 0.2s linear;
  transform: translateY(-5px);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  ${(props) =>
    props.isOpen &&
    `
    visibility: visible;
      opacity: 1;
      transform: translateY(0);
      pointer-events: all;
  }
    `}

  ${(props) =>
    props.isProductCard &&
    `
    width: 280px;
    `}

  ::after {
    content: '';
    position: absolute;
    top: -5px;
    display: block;
    background-color: var(--white);
    height: 9px;
    width: 9px;
    transform: rotate(45deg);
    border-left: solid 1px var(--gray200);
    border-top: solid 1px var(--gray200);
    ${(props) =>
      props.position === 'left'
        ? css`
            left: ${props.inlineOffset}px;
          `
        : css`
            right: ${props.inlineOffset}px;
          `}
  }
`

const TriggerContainer = styled.div<Partial<DropdownProps>>`
  position: relative;
  z-index: 1;
`

export type NonHTMLProps = {
  children?:
    | React.ReactNode
    | (({ close }: { close(): void }) => React.ReactNode)
  Trigger: React.FunctionComponent<DropdownTriggerProps>
  position?: 'left' | 'right'
  fillParent?: boolean
  onClose?: (closeDropdownProps?: CloseDropdownProps) => void
  itemClickToClose?: boolean //set to TRUE to have the dropdown close when you click on a dropdown item
  inlineOffset?: number
  blockOffset?: number
  containerStyle?: React.CSSProperties
}

export type DropdownProps = React.ComponentProps<typeof DropdownList> &
  NonHTMLProps & {
    isOpen?: boolean
    onClose?: (closeDropdownProps?: CloseDropdownProps) => void
    usePortal?: boolean
  }

const Dropdown = ({
  children,
  Trigger,
  position = 'left',
  fillParent,
  onClose,
  itemClickToClose,
  inlineOffset = 20,
  blockOffset = 10,
  css: _css,
  isOpen: _isOpen,
  containerStyle,
  usePortal = false,
  ...rest
}: DropdownProps) => {
  const [isOpen, setIsOpen] = React.useState(_isOpen)
  const backdropRef = React.useRef<HTMLDivElement>(null)
  const triggerContainerRef = React.useRef<HTMLDivElement>(null)
  const dropdownListRef = React.useRef<HTMLDivElement>(null)
  const [triggerRect, setTriggerRect] = React.useState<DOMRect>()

  inlineOffset = inlineOffset ?? 20

  const toggleDropdown = () => {
    setIsOpen((o) => {
      if (!o) {
        const rect = triggerContainerRef.current?.getBoundingClientRect()
        setTriggerRect(rect)
      } else {
        closeDropdown({ closedBy: 'trigger' })
      }
      return !o
    })
  }

  const closeDropdown = (props?: CloseDropdownProps) => {
    setIsOpen((o) => {
      if (o) onClose?.(props)
      return false
    })
  }

  // Clicking from one dropdown to another was leaving the dropdown open
  // This closes the dropdown when clicking outside of it
  React.useEffect(() => {
    const handleBodyClick = (e: MouseEvent) => {
      if (
        !triggerContainerRef.current?.contains(e.target as Node) &&
        (!dropdownListRef.current?.contains(e.target as Node))
      ) {
        closeDropdown({ closedBy: 'click outside', e })
      }
    }

    window?.document.body.addEventListener('mousedown', handleBodyClick)

    return () => {
      window?.document.body.removeEventListener('mousedown', handleBodyClick)
    }
  }, [])

  React.useEffect(() => {
    setIsOpen(_isOpen)
  }, [_isOpen])

  useDropdownEvents({
    triggerRef: triggerContainerRef,
    dropdownListRef,
    onClose: closeDropdown,
    itemClickToClose: itemClickToClose ?? false,
  })

  return (
    <DropdownBackdrop
      css={_css}
      className='dispense-dropdown-backdrop'
      ref={backdropRef}
      position={position}
      fillParent={fillParent}
      isOpen={isOpen}
      data-testid='dropdown-backdrop'
    >
      <DropdownContainer
        className='dispense-dropdown-container'
        fillParent={fillParent}
        position={position}
        data-testid='dropdown-container'
        style={containerStyle}
      >
        <TriggerContainer
          className='dispense-dropdown-trigger'
          position={position}
          ref={triggerContainerRef}
          data-testid='dropdown-trigger-container'
          style={{ width: '100%' }}
        >
          <Trigger
            toggleDropdown={toggleDropdown}
            isOpen={isOpen}
            position={position}
            data-testid='dropdown-trigger'
          />
        </TriggerContainer>
        {!usePortal && (
          <DropdownList
            className='dispense-dropdown-menu'
            {...rest}
            isOpen={isOpen}
            inlineOffset={inlineOffset}
            blockOffset={blockOffset}
            position={position}
            fillParent={fillParent}
            ref={dropdownListRef}
            data-testid='dropdown-list'
          >
            {typeof children === 'function'
              ? children({ close: closeDropdown })
              : children}
          </DropdownList>
        )}
        {usePortal && isOpen && (
          <DropdownPortal
            triggerRect={triggerRect}
            position={position}
            blockOffset={blockOffset}
          >
            <DropdownList
              className='dispense-dropdown-menu'
              {...rest}
              isOpen={isOpen}
              usePortal={usePortal}
              inlineOffset={inlineOffset}
              blockOffset={blockOffset}
              position={position}
              fillParent={fillParent}
              ref={dropdownListRef}
              data-testid='dropdown-list'
            >
              {typeof children === 'function'
                ? children({ close: closeDropdown })
                : children}
            </DropdownList>
          </DropdownPortal>
        )}
      </DropdownContainer>
    </DropdownBackdrop>
  )
}

export default Dropdown
